/**
 * useAutosave — universal form autosave + resume-after-sleep.
 * Iter 69.
 *
 * Behaviour:
 *   • Saves the `value` to localStorage under `key` every `interval` ms
 *     (default 1500 ms).
 *   • On mount, if a non-empty draft exists under `key`, it's returned via
 *     `restore()` so the parent can apply it (e.g. after the OS sleeps).
 *   • Exposes `lastSavedAt` so the UI can show "Autosaved 12s ago".
 *   • Exposes `clear()` to wipe the draft (call after a successful network save).
 *
 * Designed to be drop-in compatible with any controlled form. Just pair it
 * with your useState hook:
 *
 *   const [form, setForm] = useState(EMPTY);
 *   const { lastSavedAt, restore, clear, hasDraft } = useAutosave(`interview:${pid}`, form);
 *   useEffect(() => {
 *     const d = restore();
 *     if (d) setForm(f => ({ ...f, ...d }));
 *   }, [restore]);
 */
import { useEffect, useRef, useState, useCallback } from 'react';

const KEY_PREFIX = 'physiogenic.autosave.v1.';

export default function useAutosave(key, value, opts = {}) {
  const { interval = 1500, enabled = true } = opts;
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const valueRef = useRef(value);
  valueRef.current = value;

  // Tick autosave every `interval` ms (avoids saving on every keystroke).
  useEffect(() => {
    if (!enabled || !key) return;
    const id = setInterval(() => {
      try {
        const v = valueRef.current;
        if (v == null) return;
        const stored = JSON.stringify({ at: Date.now(), value: v });
        localStorage.setItem(KEY_PREFIX + key, stored);
        setLastSavedAt(Date.now());
      } catch {
        // localStorage may be unavailable (private window). Fail silently.
      }
    }, interval);
    return () => clearInterval(id);
  }, [key, interval, enabled]);

  const restore = useCallback(() => {
    if (!key) return null;
    try {
      const raw = localStorage.getItem(KEY_PREFIX + key);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      // Ignore drafts older than 7 days — likely stale from a different case.
      if (Date.now() - (parsed.at || 0) > 7 * 24 * 60 * 60 * 1000) {
        localStorage.removeItem(KEY_PREFIX + key);
        return null;
      }
      return parsed.value;
    } catch {
      return null;
    }
  }, [key]);

  const clear = useCallback(() => {
    if (!key) return;
    try { localStorage.removeItem(KEY_PREFIX + key); } catch {}
    setLastSavedAt(null);
  }, [key]);

  const hasDraft = useCallback(() => {
    if (!key) return false;
    try {
      const raw = localStorage.getItem(KEY_PREFIX + key);
      return !!raw;
    } catch {
      return false;
    }
  }, [key]);

  return { lastSavedAt, restore, clear, hasDraft };
}

/**
 * AutosaveIndicator — tiny chip that shows "Autosaved 12s ago" + a clear
 * button. Drop it next to the save button on any form.
 */
export function AutosaveIndicator({ lastSavedAt, onClear, testid = 'autosave-indicator' }) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 10000);
    return () => clearInterval(id);
  }, []);

  if (!lastSavedAt) return null;
  const sec = Math.max(1, Math.floor((Date.now() - lastSavedAt) / 1000));
  const label = sec < 60 ? `${sec}s ago` : sec < 3600 ? `${Math.floor(sec / 60)}m ago` : `${Math.floor(sec / 3600)}h ago`;
  return (
    <div className="inline-flex items-center gap-1.5 text-[10px] text-fg-muted" data-testid={testid} title={`Draft auto-saved locally; will resume after sleep / refresh. Last save: ${new Date(lastSavedAt).toLocaleTimeString()}`} key={tick}>
      <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#10A862] animate-pulse"/>
      Autosaved {label}
    </div>
  );
}
